/*
  <keuzehulp-quiz>
  Wraps the RevenueHunt inline quiz:
  - injects the Sanaslank skin into the quiz's open shadow root
  - shows a skeleton until the quiz has rendered and is skinned
  - drives the theme step progress (goal -> preference -> advice)
  - keeps the active question in view on small screens
*/
if (!customElements.get('keuzehulp-quiz')) {
  class KeuzehulpQuiz extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.keuzehulp-steps__item'));
      this.status = this.querySelector('.keuzehulp-steps__status');
      this.steps = this.querySelector('.keuzehulp-steps');
      this.renderer = this.querySelector('quiz-renderer');
      this.skinUrl = this.querySelector('template[data-skin-url]')?.innerHTML.trim();
      this.depth = 0;
      this.step = -1;

      this.style.setProperty('--kh-steps', this.items.length || 3);
      this.setStep(0);

      if (!this.renderer) {
        this.reveal();
        return;
      }

      this.instanceId = this.renderer.getAttribute('data-quiz-instance-id');
      this.onMessage = this.onMessage.bind(this);
      window.addEventListener('message', this.onMessage);

      // Never keep the skeleton forever, even if the quiz fails to render.
      this.revealTimer = setTimeout(() => this.reveal(), 8000);
      this.waitForShadowRoot();
    }

    disconnectedCallback() {
      window.removeEventListener('message', this.onMessage);
      this.rootObserver?.disconnect();
      this.hostObserver?.disconnect();
      clearTimeout(this.revealTimer);
    }

    findQuizRoot() {
      const scopes = [this.renderer, this.renderer.shadowRoot].filter(Boolean);
      for (const scope of scopes) {
        const host = scope.querySelector('#shadow-quiz');
        if (host?.shadowRoot) return host.shadowRoot;
      }
      return null;
    }

    waitForShadowRoot() {
      const attach = () => {
        const root = this.findQuizRoot();
        if (!root) return false;
        this.hostObserver?.disconnect();
        this.onQuizRoot(root);
        return true;
      };
      if (attach()) return;

      this.hostObserver = new MutationObserver(attach);
      this.hostObserver.observe(this.renderer, { childList: true, subtree: true });
      if (this.renderer.shadowRoot) {
        this.hostObserver.observe(this.renderer.shadowRoot, { childList: true, subtree: true });
      }
    }

    onQuizRoot(root) {
      this.root = root;
      this.injectSkin();

      let pending = false;
      this.rootObserver = new MutationObserver(() => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(() => {
          pending = false;
          // The quiz may replace its stylesheets; make sure the skin stays.
          if (!root.getElementById('kh-skin')) this.injectSkin();
          this.sync();
        });
      });
      this.rootObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'class'],
      });
      // product images on the results page change the height without a DOM mutation
      root.addEventListener('load', () => this.isResult() && this.fitResultHeight(), true);
      this.sync();
    }

    injectSkin() {
      if (!this.skinUrl) {
        this.skinned = true;
        return;
      }
      const link = document.createElement('link');
      link.id = 'kh-skin';
      link.rel = 'stylesheet';
      link.href = this.skinUrl;
      link.addEventListener('load', () => {
        this.skinned = true;
        this.sync();
      });
      link.addEventListener('error', () => {
        this.skinned = true;
        this.sync();
      });
      this.root.appendChild(link);
    }

    isResult() {
      return Boolean(this.root?.querySelector('.results, .results-wrapper'));
    }

    hasQuestion() {
      return Boolean(this.root?.querySelector('.question, .question-wrapper'));
    }

    // Derive the step from the quiz itself, so a resumed quiz (saved progress) is right too.
    sync() {
      if (!this.root) return;
      const last = this.items.length - 1;

      if (this.isResult()) {
        this.setStep(last, true);
        this.fitResultHeight();
      } else if (this.hasQuestion()) {
        const back = this.root.querySelector('.navigation-bar__button');
        if (back) this.depth = back.disabled ? 0 : Math.max(this.depth, 1);
        this.setStep(Math.min(this.depth, Math.max(last - 1, 0)));
      }

      if (this.skinned && (this.isResult() || this.hasQuestion())) this.reveal();
    }

    // RevenueHunt reports a results height a few px short, which leaves a tiny inner scroll.
    // Grow the renderer to the real content height instead.
    fitResultHeight() {
      const main = this.root.querySelector('main.quiz.results, .quiz-result');
      if (!main) return;
      const extra = main.scrollHeight - main.clientHeight;
      if (extra <= 0) return;
      const height = this.renderer.offsetHeight + extra;
      this.renderer.style.height = `${height}px`;
      this.renderer.style.minHeight = `${height}px`;
    }

    setStep(index, isResult = false) {
      this.dataset.state = this.dataset.state === 'loading' ? 'loading' : isResult ? 'result' : 'question';
      this.resultState = isResult;
      if (index === this.step && this.items.length) return;
      this.step = index;

      const total = this.items.length;
      this.items.forEach((item, i) => {
        item.classList.toggle('is-done', i < index);
        if (i === index) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      this.style.setProperty('--kh-progress', total > 1 ? index / (total - 1) : 1);

      const label = this.items[index]?.querySelector('.keuzehulp-steps__label')?.textContent.trim();
      if (this.status && label) this.status.textContent = `Stap ${index + 1} van ${total}: ${label}`;
    }

    reveal() {
      clearTimeout(this.revealTimer);
      this.dataset.state = this.resultState ? 'result' : 'question';
    }

    onMessage(event) {
      const data = event.data;
      if (
        !data ||
        typeof data !== 'object' ||
        data.channel !== 'revenuehunt:quiz-renderer' ||
        data.instanceId !== this.instanceId
      ) {
        return;
      }

      switch (data.type) {
        case 'nextQuestion':
          this.depth += 1;
          this.keepInView();
          break;
        case 'previousQuestion':
          this.depth = Math.max(0, this.depth - 1);
          this.keepInView();
          break;
        case 'scrollToTop':
          this.depth = 0;
          this.keepInView();
          break;
        case 'isResult':
          this.keepInView();
          break;
        case 'resultsHeight':
          // the inline quiz script applies this height after us; correct it afterwards
          break;
        default:
          return;
      }
      // Let the quiz render the new page before reading its DOM.
      setTimeout(() => this.sync(), 80);
    }

    // Only scroll when the top of the quiz has left the viewport (long answers / mobile).
    keepInView() {
      const target = this.steps || this;
      const top = target.getBoundingClientRect().top;
      if (top >= 0 && top < window.innerHeight * 0.4) return;
      const header = document.querySelector('header[is="sticky-header"], .header-section');
      const offset = (header?.getBoundingClientRect().height || 0) + 12;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: window.scrollY + top - offset, behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  customElements.define('keuzehulp-quiz', KeuzehulpQuiz);
}
